import { debounce } from 'lodash-es';
import { onUnmounted } from 'vue';

export const MIN_TOUCH_DURATION = 500;
export const DUPLICATE_INTENT_DEBOUNCE_DURATION = 500;

type TimeoutHandler = ReturnType<typeof setTimeout>;
export type NewTabIntentEvent = MouseEvent | TouchEvent;
type Callback = (event: NewTabIntentEvent) => void;

// Modified click (for example: cmd-click, ctrl-click, or shift-click).
function getClickHandler(callback: Callback) {
  return function (event: MouseEvent) {
    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/metaKey
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      callback(event);
    }
  };
}

// Middle Click
function getAuxClickHandler(callback: Callback) {
  return function (event: MouseEvent) {
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/auxclick_event
    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
    if (event.button === 1) {
      callback(event);
    }
  };
}

// Right-click, the assumption is that the user will select the "Open in new tab" option.
function getContextMenuHandler(callback: Callback) {
  return function (event: MouseEvent) {
    callback(event);
  };
}

// Workaround for the absence of the 'contextmenu' event on mobile iOS.
// Use touch events to detect a long press.
function getTouchEventHandlers(callback: Callback) {
  let touchTimeoutHandle: TimeoutHandler | undefined;

  function clearTouchTimeout() {
    clearTimeout(touchTimeoutHandle);
    touchTimeoutHandle = undefined;
  }

  function touchStartHandler(event: TouchEvent) {
    clearTouchTimeout();
    touchTimeoutHandle = setTimeout(() => callback(event), MIN_TOUCH_DURATION);
  }

  function touchEndHandler() {
    clearTouchTimeout();
  }

  function touchMoveHandler() {
    clearTouchTimeout();
  }

  function touchCancelHandler() {
    clearTouchTimeout();
  }

  return {
    touchStartHandler,
    touchEndHandler,
    touchMoveHandler,
    touchCancelHandler,
    clearTouchTimeout,
  };
}

export function useDetectNewTabIntent(callback: Callback) {
  // Android devices will fire both the contextmenu and the touch events, so avoid emitting twice.
  const debouncedCallback = debounce(callback, DUPLICATE_INTENT_DEBOUNCE_DURATION, {
    leading: true,
    trailing: false,
  });

  const {
    touchStartHandler,
    touchEndHandler,
    touchMoveHandler,
    touchCancelHandler,
    clearTouchTimeout,
  } = getTouchEventHandlers(debouncedCallback);

  onUnmounted(() => {
    // Cancel any pending long-press timeout and clear lodash's internal
    // debounce window timer so no timers outlive the host component.
    clearTouchTimeout();
    debouncedCallback.cancel();
  });

  return {
    handleClick: getClickHandler(debouncedCallback),
    handleAuxClick: getAuxClickHandler(debouncedCallback),
    handleContextMenu: getContextMenuHandler(debouncedCallback),
    touchStartHandler,
    touchEndHandler,
    touchMoveHandler,
    touchCancelHandler,
  };
}
